/**
 * Payments hooks (same contract as the clients hooks).
 * Single source of truth: authenticated sessions only ever see API data —
 * errors yield `isError` with empty data, never a fallback.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAYMENTS_QUERY_PREFIX, REPORTS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Payment, PaymentStatus } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import {
  patchInList,
  prependToList,
  removeFromList,
  restoreSnapshot,
  snapshotList,
} from "@/lib/optimistic-cache";
import { paymentsApi } from "./payments.api";
import type { ApiPayment } from "./payments.adapter";
import { toPayment, toPaymentWriteBody, type PaymentInput } from "./payments.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };
const ARCHIVED_PARAMS = { archived: true, limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UsePaymentsResult {
  payments: Payment[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function usePayments(): UsePaymentsResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.paymentsList(LIST_PARAMS),
    queryFn: () => paymentsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data) return { payments: query.data.data.map(toPayment), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { payments: [], isLoading: false, isError: true, isFetching: false, source: "api", error: query.error, refetch: query.refetch };
  return { payments: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

export function usePayment(paymentId: string): {
  payment: Payment | null;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.payment(paymentId),
    queryFn: () => paymentsApi.getById(paymentId),
    enabled: authed && !!paymentId,
  });

  if (query.data) return { payment: toPayment(query.data), isLoading: false, isError: false, source: "api", error: null };
  if (query.isError) return { payment: null, isLoading: false, isError: true, source: "api", error: query.error };
  return { payment: null, isLoading: query.isLoading, isError: false, source: "api", error: null };
}

function useInvalidatePayments() {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: PAYMENTS_QUERY_PREFIX }),
    qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX }),
  ]);
}

export function useCreatePayment() {
  const qc = useQueryClient();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (input: PaymentInput) => {
      return paymentsApi.create(toPaymentWriteBody(input));
    },
    // Post-response (not speculative): the server-confirmed payment appears at
    // the top of the list at once, grafting the client name the API omits.
    onSuccess: (data, variables) => {
      const created = data as ApiPayment;
      prependToList<ApiPayment>(qc, queryKeys.paymentsList(LIST_PARAMS), {
        ...created,
        client: created.client ?? { name: variables.clientName },
      });
      void invalidate();
    },
  });
}

export function useUpdatePayment() {
  const qc = useQueryClient();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: PaymentInput }) => {
      return paymentsApi.update(id, toPaymentWriteBody(input));
    },
    onSuccess: (data, { id }) => {
      const updated = data as ApiPayment;
      patchInList<ApiPayment>(qc, queryKeys.paymentsList(LIST_PARAMS), id, updated);
      qc.setQueryData<ApiPayment>(queryKeys.payment(id), (old) =>
        old ? { ...old, ...updated } : old,
      );
      void invalidate();
    },
  });
}

export function useUpdatePaymentStatus() {
  const qc = useQueryClient();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PaymentStatus }) => {
      return paymentsApi.update(id, { status });
    },
    // Reflect the new status (and server-set receivedAt) in the row immediately.
    onSuccess: (data, { id }) => {
      const updated = data as ApiPayment;
      patchInList<ApiPayment>(qc, queryKeys.paymentsList(LIST_PARAMS), id, updated);
      qc.setQueryData<ApiPayment>(queryKeys.payment(id), (old) =>
        old ? { ...old, ...updated } : old,
      );
      void invalidate();
    },
  });
}

export function useArchivePayment() {
  const qc = useQueryClient();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (id: string) => {
      return paymentsApi.archive(id);
    },
    // Optimistic: the row leaves the active list at once; rolled back on error.
    onMutate: async (id: string) => {
      const listKey = queryKeys.paymentsList(LIST_PARAMS);
      await qc.cancelQueries({ queryKey: listKey });
      const snapshot = snapshotList(qc, [listKey]);
      removeFromList<ApiPayment>(qc, listKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useRestorePayment() {
  const qc = useQueryClient();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (id: string) => {
      return paymentsApi.restore(id);
    },
    onMutate: async (id: string) => {
      const archivedKey = queryKeys.paymentsList(ARCHIVED_PARAMS);
      await qc.cancelQueries({ queryKey: archivedKey });
      const snapshot = snapshotList(qc, [archivedKey]);
      removeFromList<ApiPayment>(qc, archivedKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useArchivedPayments() {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.paymentsList(ARCHIVED_PARAMS),
    queryFn: () => paymentsApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data) return { payments: query.data.data.map(toPayment), isLoading: false, isError: false, refetch: query.refetch };
  if (query.isError) return { payments: [] as Payment[], isLoading: false, isError: true, refetch: query.refetch };
  return { payments: [] as Payment[], isLoading: query.isLoading, isError: false, refetch: query.refetch };
}
